import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop, Watch } from 'vue-property-decorator';
import { MtpStatus } from '~/utils/interfaces';
import { safeInvertedDifferenceOfDecimal, safeSum, zeroIfNaN } from '~/utils/math-helper';
import { cellValueClass, tableCellTextFormat } from '~/utils/table-helper';
import {
    IybrGlobalIdsCollapsibleTableData,
    ProjectTypes
} from '~/components/in-year-budget-requests/create-in-year-budget-request/create-iybr-form/iybr-global-ids-collapsible/iybr-global-ids-collapsible';
import { IybrNewProjectCollapsibleFormData } from '~/components/in-year-budget-requests/create-in-year-budget-request/create-iybr-form/iybr-new-project-collapsible/iybr-new-project-collapsible';
import { checkIfAllArrayItemsAreEqualTo } from '~/utils/array-utils';
import { CompensationTableTotals } from '~/components/in-year-budget-requests/create-in-year-budget-request/create-iybr-form/iybr-compensation-table/iybr-compensation-table';
import {
    CurrencyRate,
    TABLE_FIELDS,
    TABLE_KEYS
} from '~/components/in-year-budget-requests/create-in-year-budget-request/create-iybr-form/iybr-new-project-collapsible/create-iybr-form-utils';
import { Data } from '~/utils';
import { isNewProjectFlow } from '~/components/in-year-budget-requests/utils/request-type-groups';
import { PuiTableVendorOptions } from '~/models/libraries/pebble-ui';
import {
    CompensationRequiredTotals
} from '~/components/in-year-budget-requests/create-in-year-budget-request/create-iybr-form/create-iybr-form-model';
import {
    totalBudgetToBeApprovedEur
} from '~/components/in-year-budget-requests/utils/budget-to-be-approved-eur';

export type SumBudgetRequestData = {
    total: number,
    approved: number,
    pending: number
};

@Component({})
export default class IybrCostTableComponent extends Vue {
    @Prop()
    private currencyName!: string;

    @Prop()
    private globalIdsCollapsibleTableData?: IybrGlobalIdsCollapsibleTableData;

    @Prop()
    private newProjectFormData?: IybrNewProjectCollapsibleFormData;

    @Prop()
    private compensationTotals?: CompensationTableTotals;

    @Prop()
    private requestType?: string;

    @Prop()
    private projectType: string;

    @Prop({ default: () => new Date().getFullYear() })
    private mtpYear?: number;

    private readonly tableInputFields = [
        TABLE_KEYS.spentAfterYear,
        TABLE_KEYS.spentYearPlus1,
        TABLE_KEYS.spentYearPlus2,
        TABLE_KEYS.spentYearPlus3
    ];

    private readonly projectFields = [
        TABLE_KEYS.spentYearMinus1,
        TABLE_KEYS.spentYear,
        TABLE_KEYS.spentAfterYear
    ];

    private readonly totalKeys = [
        ...this.projectFields,
        TABLE_KEYS.spentYearPlus1,
        TABLE_KEYS.spentYearPlus2,
        TABLE_KEYS.spentYearPlus3
    ];

    private readonly ONE_MILLION = 1000000;
    private cellValueClass = cellValueClass;
    private tableCellTextFormat = tableCellTextFormat;

    public get formTotal(): number {
        let totals: number[];

        if (!isNewProjectFlow(this.requestType)) {
            const budgetDataEntries = Object.values(this.globalIdsCollapsibleTableData).map(entry => entry.budgetData);

            totals = this.totalKeys.map((key) => budgetDataEntries.map((entry) => {
                if (this.projectType === ProjectTypes.NonProject && this.projectFields.includes(key)) {
                    return 0;
                }
                return entry[key];
            }).reduce(safeSum, 0));
        } else {
            totals = this.totalKeys.map((key) => {
                if (this.projectType === ProjectTypes.NonProject && this.projectFields.includes(key)) {
                    return 0;
                }
                return this.newProjectFormData[key];
            });
        }

        this.updateConvertedTotalCost();
        return totals.reduce(safeSum, 0);
    }

    private get globalIdCount(): number {
        return this.globalIdsCollapsibleTableData ? Object.values(this.globalIdsCollapsibleTableData).length : 0;
    }

    private get columnNameMap(): Record<string, string> {
        return {
            'MTP-1': 'spendUntilEndOfYear',
            MTP: 'forecast',
            previousYears: 'forecast',
            'MTP+1': 'new1',
            'MTP+2': 'new2',
            'MTP+3': 'new3',
            ff: 'after',
            mtpTotal: 'mtpTotal',
            total: 'total',
        };
    }

    private get columns(): Record<string, string> {
        return {
            title: `Currency: ${this.currencyName}`,
            'MTP-1': `${this.mtpYear - 1}`,
            MTP: `${this.mtpYear}`,
            'previousYears': `Approved Budget Before ${this.mtpYear + 1}`,
            'MTP+1': `${this.mtpYear + 1}`,
            'MTP+2': `${this.mtpYear + 2}`,
            'MTP+3': `${this.mtpYear + 3}`,
            ff: 'FF.',
            mtpTotal: 'MTP Total',
            total: 'Overall Total',
        };
    }

    private get shownColumns(): string[] {
        const hiddenColumns = ['MTP-1', 'MTP'];

        if (this.projectType === ProjectTypes.NonProject) {
            hiddenColumns.push('previousYears');
        }

        return Object.keys(this.columns)
            .filter(key => !hiddenColumns.includes(key))
            .map(key => this.columns[key]);
    }

    private get rows(): Record<string, string> {
        const budgetRequestMtpStatuses = Object.keys(this.globalIdsCollapsibleTableData).map(key => this.globalIdsCollapsibleTableData[key].budgetRequestData?.planning?.mtpStatus);
        let budgetInMtpStatus = '';

        if (checkIfAllArrayItemsAreEqualTo(budgetRequestMtpStatuses, MtpStatus.APPROVED)) {
            budgetInMtpStatus = '(Approved)';
        } else if (checkIfAllArrayItemsAreEqualTo(budgetRequestMtpStatuses, MtpStatus.YELLOW_FLAGGED)) {
            budgetInMtpStatus = '(Yellow Flagged)';
        }

        return {
            mtpBudget: `Budget in MTP ${budgetInMtpStatus}`,
            totalBudget: 'Total Budget Required',
            toBeApproved: 'Budget to be approved',
            compensationAvailable: 'Compensation Available'
        };
    }

    private get isNonProjectChangeRequest(): boolean {
        return this.projectType === ProjectTypes.NonProject;
    }

    private sumBudgetRequestData(field: string): SumBudgetRequestData {
        if (this.globalIdsCollapsibleTableData) {
            const approved = Object.keys(this.globalIdsCollapsibleTableData)
                .filter(
                    (key) =>
                        this.globalIdsCollapsibleTableData[key].budgetRequestData.planning.mtpStatus ===
                        MtpStatus.APPROVED
                )
                .map((key) => this.globalIdsCollapsibleTableData[key].budgetRequestData.planning[field])
                .reduce((previous, current) => previous + current, 0);

            const pending = Object.keys(this.globalIdsCollapsibleTableData)
                .filter(
                    (key) =>
                        this.globalIdsCollapsibleTableData[key].budgetRequestData.planning.mtpStatus !==
                        MtpStatus.APPROVED
                )
                .map((key) => this.globalIdsCollapsibleTableData[key].budgetRequestData.planning[field])
                .reduce((previous, current) => previous + current, 0);

            return {
                approved,
                pending,
                total: approved + pending,
            };
        }

        return {
            approved: 0,
            pending: 0,
            total: 0,
        };
    }

    private sumBudgetCostData(field: string): number {
        if (!isNewProjectFlow(this.requestType)) {
            if (this.globalIdsCollapsibleTableData) {
                return Object.keys(this.globalIdsCollapsibleTableData)
                    .map((key) => this.globalIdsCollapsibleTableData[key].budgetData[field])
                    .reduce(safeSum, 0);
            }
        } else if (this.newProjectFormData) {
            const value = parseFloat(this.newProjectFormData[field]);
            return zeroIfNaN(value);
        }

        return 0;
    }

    private computeToBeApproved(): {
        toBeApprovedValues: Record<string, number>;
        compensationRequired: CompensationRequiredTotals;
    } | undefined {
        const budgetRequestCell = (columnName: string): number =>
            this.sumBudgetRequestData(this.columnNameMap[columnName]).total;

        const totalBudgetCell = (columnName: string): number => {
            if (columnName === 'previousYears') {
                return this.isNonProjectChangeRequest
                    ? 0
                    : this.sumBudgetCostData('spentYearMinus1') + this.sumBudgetCostData('spentYear');
            }
            const fieldByColumn: Record<string, string> = {
                'MTP-1': 'spentYearMinus1',
                MTP: 'spentYear',
                'MTP+1': 'spentYearPlus1',
                'MTP+2': 'spentYearPlus2',
                'MTP+3': 'spentYearPlus3',
                ff: 'spentAfterYear',
            };
            return this.sumBudgetCostData(fieldByColumn[columnName]);
        };

        const compensationRowCellValue = (columnName: string): number =>
            safeInvertedDifferenceOfDecimal(budgetRequestCell(columnName), totalBudgetCell(columnName));

        const compensationValues: Record<string, number> = {
            'MTP-1': compensationRowCellValue('MTP-1'),
            MTP: compensationRowCellValue('MTP'),
            previousYears: compensationRowCellValue('previousYears'),
            'MTP+1': compensationRowCellValue('MTP+1'),
            'MTP+2': compensationRowCellValue('MTP+2'),
            'MTP+3': compensationRowCellValue('MTP+3'),
            ff: compensationRowCellValue('ff'),
        };

        const toBeApprovedRowCellValue = (columnName: string): number => safeSum(
            compensationValues[columnName],
            this.sumBudgetRequestData(this.columnNameMap[columnName]).pending
        );

        const toBeApprovedValues: Record<string, number> = {
            'MTP-1': toBeApprovedRowCellValue('MTP-1'),
            MTP: toBeApprovedRowCellValue('MTP'),
            previousYears: toBeApprovedRowCellValue('previousYears'),
            'MTP+1': toBeApprovedRowCellValue('MTP+1'),
            'MTP+2': toBeApprovedRowCellValue('MTP+2'),
            'MTP+3': toBeApprovedRowCellValue('MTP+3'),
            ff: toBeApprovedRowCellValue('ff'),
        };

        return {
            toBeApprovedValues,
            compensationRequired: {
                mtpPlus1Required: compensationValues['MTP+1'],
                mtpPlus2Required: compensationValues['MTP+2'],
                mtpPlus3Required: compensationValues['MTP+3'],
            },
        };
    }

    @Watch('globalIdsCollapsibleTableData', { deep: true, immediate: true })
    @Watch('newProjectFormData', { deep: true, immediate: true })
    @Watch('compensationTotals', { deep: true })
    @Watch('requestType')
    @Watch('projectType')
    @Watch('currencyName')
    @Watch('mtpYear')
    private publishBudgetToBeApproved(): void {
        if (!this.currencyName || !this.mtpYear) {
            return;
        }

        const { toBeApprovedValues, compensationRequired } = this.computeToBeApproved();
        this.onCompensationRequiredChange(compensationRequired);

        const currencyRate = this.findCurrencyRate(this.currencyName, this.mtpYear);
        const mtpPlus1Meur = toBeApprovedValues['MTP+1'] ?? 0;
        const totalEur = totalBudgetToBeApprovedEur(
            {
                'MTP+1': toBeApprovedValues['MTP+1'],
                'MTP+2': toBeApprovedValues['MTP+2'],
                'MTP+3': toBeApprovedValues['MTP+3'],
                ff: toBeApprovedValues['ff'],
                previousYears: toBeApprovedValues['previousYears'],
            },
            currencyRate,
            !this.isNonProjectChangeRequest
        );

        this.$emit('budget-to-be-approved-updated', { mtpPlus1Meur, totalEur });
    }

    private get tableOptions(): PuiTableVendorOptions {
        const sumBudgetRequestData = (field: string): SumBudgetRequestData => this.sumBudgetRequestData(field);
        const sumBudgetCostData = (field: string): number => this.sumBudgetCostData(field);
        const isNonProjectChangeRequest = this.isNonProjectChangeRequest;

        const budgetRequestMtpTotal = sumBudgetRequestData('new1').total + sumBudgetRequestData('new2').total + sumBudgetRequestData('new3').total;
        const budgetRequestRow = {
            [this.columns['title']]: this.rows.mtpBudget,
            [this.columns['MTP-1']]: sumBudgetRequestData('spendUntilEndOfYear').total,
            [this.columns['MTP']]: sumBudgetRequestData('forecast').total,
            [this.columns['previousYears']]: isNonProjectChangeRequest ? 0 : sumBudgetRequestData('spendUntilEndOfYear').total + sumBudgetRequestData('forecast').total,
            [this.columns['MTP+1']]: sumBudgetRequestData('new1').total,
            [this.columns['MTP+2']]: sumBudgetRequestData('new2').total,
            [this.columns['MTP+3']]: sumBudgetRequestData('new3').total,
            [this.columns['ff']]: sumBudgetRequestData('after').total,
            [this.columns['mtpTotal']]: budgetRequestMtpTotal,
            [this.columns['total']]: sumBudgetRequestData('total').total,
        };

        const totalBudgetMtpTotal = sumBudgetCostData('spentYearPlus1') + sumBudgetCostData('spentYearPlus2') + sumBudgetCostData('spentYearPlus3');
        const totalBudgetRow = {
            [this.columns['title']]: this.rows.totalBudget,
            [this.columns['MTP-1']]: sumBudgetCostData('spentYearMinus1'),
            [this.columns['MTP']]: sumBudgetCostData('spentYear'),
            [this.columns['previousYears']]: isNonProjectChangeRequest ? 0 : sumBudgetCostData('spentYearMinus1') + sumBudgetCostData('spentYear'),
            [this.columns['MTP+1']]: sumBudgetCostData('spentYearPlus1'),
            [this.columns['MTP+2']]: sumBudgetCostData('spentYearPlus2'),
            [this.columns['MTP+3']]: sumBudgetCostData('spentYearPlus3'),
            [this.columns['ff']]: sumBudgetCostData('spentAfterYear'),
            [this.columns['mtpTotal']]: totalBudgetMtpTotal,
            [this.columns['total']]: this.formTotal,
        };

        const { toBeApprovedValues } = this.computeToBeApproved();

        let toBeApprovedTotal = toBeApprovedValues['MTP+1'] + toBeApprovedValues['MTP+2'] + toBeApprovedValues['MTP+3'];

        if (!isNonProjectChangeRequest) {
            toBeApprovedTotal += toBeApprovedValues['ff'] + toBeApprovedValues['previousYears'];
        }

        const toBeApprovedMtpTotal = toBeApprovedValues['MTP+1'] + toBeApprovedValues['MTP+2'] + toBeApprovedValues['MTP+3'];

        const toBeApprovedRow = {
            [this.columns['title']]: this.rows.toBeApproved,
            [this.columns['MTP-1']]: toBeApprovedValues['MTP-1'],
            [this.columns['MTP']]: toBeApprovedValues['MTP'],
            [this.columns['previousYears']]: 0,
            [this.columns['MTP+1']]: toBeApprovedValues['MTP+1'],
            [this.columns['MTP+2']]: toBeApprovedValues['MTP+2'],
            [this.columns['MTP+3']]: toBeApprovedValues['MTP+3'],
            [this.columns['ff']]: toBeApprovedValues['ff'],
            [this.columns['mtpTotal']]: toBeApprovedMtpTotal,
            [this.columns['total']]: toBeApprovedTotal,
        };

        const frontOptionalRows = [];
        const backOptionalRows = [];

        if (!isNewProjectFlow(this.requestType) && this.globalIdCount !== 1) {
            frontOptionalRows.push(budgetRequestRow, totalBudgetRow);
        }

        if (this.compensationTotals && this.compensationTotals.hasCompensationEntries) {
            const compensationMtpAvailableTotal =
                this.compensationTotals.mtpYearPlus1Total +
                this.compensationTotals.mtpYearPlus2Total +
                this.compensationTotals.mtpYearPlus3Total;
            const compensationAvailable: Record<string, string | number> = {
                [this.columns['title']]: this.rows.compensationAvailable,
                [this.columns['MTP+1']]: this.compensationTotals.mtpYearPlus1Total,
                [this.columns['MTP+2']]: '-',
                [this.columns['MTP+3']]: '-',
                [this.columns['ff']]: '-',
                [this.columns['mtpTotal']]: compensationMtpAvailableTotal,
                [this.columns['total']]: this.compensationTotals.totalCompensation,
            };
            if (!isNonProjectChangeRequest) {
                compensationAvailable[this.columns['previousYears']] = '-';
            }

            backOptionalRows.push(compensationAvailable);
        }

        const beforeYear = this.mtpYear + 1;

        this.$nextTick(() => this.publishBudgetToBeApproved());

        return {
            data: [...frontOptionalRows, toBeApprovedRow, ...backOptionalRows],
            options: {
                sortable: [],
                headings: {
                    [`Approved Budget Before ${beforeYear}`](h) {
                        return h('span', ['Approved Budget', h('br'), `Before ${beforeYear}`])
                    },
                },
            },
            columns: Object.values(this.shownColumns),
        };
    }

    private updateConvertedTotalCost(): void {
        if (!this.currencyName || !this.mtpYear) {
            return;
        }

        const currencyRate = this.findCurrencyRate(this.currencyName, this.mtpYear);
        if (!currencyRate) {
            return;
        }

        let inputTotals: number[];

        if (!isNewProjectFlow(this.requestType)) {
            const budgetDataEntries = Object.values(this.globalIdsCollapsibleTableData ?? {}).map(entry => entry.budgetData);

            inputTotals = this.totalKeys.map((key) => budgetDataEntries.map((entry) => {
                if (this.projectType === ProjectTypes.NonProject && this.projectFields.includes(key)) {
                    return 0;
                }
                const rate = currencyRate[TABLE_FIELDS[key]] ?? currencyRate.forecast;
                return entry[key] * this.ONE_MILLION * rate;
            }).reduce(safeSum, 0));
        } else {
            inputTotals = this.totalKeys.map((key) => {
                if (this.projectType === ProjectTypes.NonProject && this.projectFields.includes(key)) {
                    return 0;
                }
                const rate = currencyRate[TABLE_FIELDS[key]] ?? currencyRate.forecast;
                return this.newProjectFormData[key] * this.ONE_MILLION * rate;
            });
        }

        const inputsTotalCost = inputTotals.reduce(safeSum, 0);
        this.$emit('total-cost-updated', inputsTotalCost);
    }

    private findCurrencyRate(baseCurrency: string, year: number): CurrencyRate {
        const currencyRates: CurrencyRate[] = Data.Instance.constants.currencyRates;
        if (currencyRates) {
            return currencyRates.find(currencyRate =>
                currencyRate.year === year &&
                currencyRate.baseCurrency === baseCurrency &&
                currencyRate.targetCurrency === 'mEUR');
        }
    }

    private onCompensationRequiredChange(compensationRequiredTotals: CompensationRequiredTotals): void {
        this.$emit('change:compensation-required', compensationRequiredTotals);
    }
}
